import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { membersAPI, paymentsAPI, invoiceAPI } from '../../services/api';

function PdfPreviewModal({ url, title, filename, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  function handleDownload() {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm">
      <div className="flex shrink-0 items-center justify-between gap-3 bg-white px-4 py-3 shadow">
        <p className="truncate text-sm font-semibold text-slate-800">{title}</p>
        <div className="flex shrink-0 items-center gap-2">
          <button onClick={handleDownload}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700">
            ⬇ Download
          </button>
          <button onClick={onClose}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            ✕ Close
          </button>
        </div>
      </div>
      <iframe src={url} title={title} className="min-h-0 flex-1 w-full bg-slate-200" />
    </div>,
    document.body
  );
}

export default function MemberReceipts() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // { url, title, filename }
  const [viewing, setViewing] = useState(null); // invoice id being loaded

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const member = await membersAPI.me();
        const resp = await paymentsAPI.getByMember(member.id);
        const paid = (resp.invoices || []).filter(
          (p) => String(p.paystatus || '').toUpperCase() === 'PAID'
        );
        paid.sort((a, b) => (b.fee_year || 0) - (a.fee_year || 0));
        setInvoices(paid);
      } catch (e) {
        setError(e.response?.data?.error || 'Failed to load receipts.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  async function openPreview(inv) {
    setViewing(inv.id);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`/api/invoices/${inv.id}/receipt`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setPreview({ url, title: `Receipt — ${inv.reference_no}`, filename: `AWTF-Receipt-${inv.id}.pdf` });
    } catch (e) {
      console.error(e);
    } finally {
      setViewing(null);
    }
  }

  function closePreview() {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  return (
    <div className="space-y-6">
      {preview && <PdfPreviewModal {...preview} onClose={closePreview} />}

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">My Receipts</h1>
        {!loading && !error && (
          <span className="rounded-2xl bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-700">
            {invoices.length} {invoices.length === 1 ? 'receipt' : 'receipts'}
          </span>
        )}
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
        <span className="mt-0.5 text-lg">🧾</span>
        <p>
          These receipts confirm all payments you have made to AWTF. Each receipt is official
          proof of your payment.
        </p>
      </div>

      {loading && (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          Loading receipts...
        </div>
      )}

      {error && !loading && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && invoices.length === 0 && (
        <div className="rounded-[28px] border border-slate-200 bg-white p-10 text-center">
          <p className="text-2xl">🧾</p>
          <p className="mt-3 font-semibold text-slate-700">No receipts yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Receipts will appear here after your AWTF fee payment is confirmed by CRDB Bank.
          </p>
        </div>
      )}

      {!loading && !error && invoices.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-[28px] bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)] md:block">
            <table className="w-full">
              <thead className="border-b bg-slate-50">
                <tr>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Year</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Reference No</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Amount</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Status</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-b-0 hover:bg-slate-50/60">
                    <td className="px-5 py-4 text-sm font-semibold text-slate-900">{inv.fee_year}</td>
                    <td className="px-5 py-4 font-mono text-sm text-slate-700">{inv.reference_no}</td>
                    <td className="px-5 py-4 text-sm text-slate-700">
                      TZS {Number(inv.fee || 0).toLocaleString()}
                    </td>
                    <td className="px-5 py-4">
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                        PAID
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => openPreview(inv)}
                        disabled={viewing !== null}
                        className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {viewing === inv.id ? '⏳ Loading...' : '🧾 View Receipt'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {invoices.map((inv) => (
              <div key={inv.id} className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-900">AWTF Fee {inv.fee_year}</p>
                    <p className="mt-0.5 font-mono text-xs text-slate-500">{inv.reference_no}</p>
                  </div>
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 whitespace-nowrap">
                    PAID
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  TZS {Number(inv.fee || 0).toLocaleString()}
                </p>
                <button
                  type="button"
                  onClick={() => openPreview(inv)}
                  disabled={viewing !== null}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {viewing === inv.id ? '⏳ Loading...' : '🧾 View Receipt'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
